// Utility functions to eliminate code duplication

// Export navigation utilities
export * from './navigation';

// Export image utilities
export * from './imageUtils';

// Export security utilities
export * from './securityMiddleware';
export * from './securityConfig';
export * from './secureSupabaseClient';

export const formatPrice = (
  price: number,
  currency: string = "USD"
): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(price);
};

export const calculateDiscount = (
  originalPrice: number,
  salePrice: number
): number => {
  return Math.round(((originalPrice - salePrice) / originalPrice) * 100);
};

export const formatRating = (rating: number): string => {
  return rating.toFixed(1);
};

export const truncateText = (text: string, maxLength: number): string => {
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

export const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w ]+/g, "")
    .replace(/ +/g, "-");
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const classNames = (
  ...classes: (string | undefined | null | false)[]
): string => {
  return classes.filter(Boolean).join(" ");
};

export const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/\s/g, ""));
};

export const formatDate = (
  date: Date,
  format: "short" | "long" | "relative" = "short"
): string => {
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  switch (format) {
    case "relative":
      if (diffDays === 0) return "Today";
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
      return `${Math.ceil(diffDays / 30)} months ago`;

    case "long":
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

    default:
      return date.toLocaleDateString("en-US");
  }
};

export const sortArray = <T>(
  array: T[],
  key: keyof T,
  direction: "asc" | "desc" = "asc"
): T[] => {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    if (aVal < bVal) return direction === "asc" ? -1 : 1;
    if (aVal > bVal) return direction === "asc" ? 1 : -1;
    return 0;
  });
};

export const filterArray = <T>(
  array: T[],
  filters: Partial<Record<keyof T, any>>
): T[] => {
  return array.filter((item) =>
    Object.entries(filters).every(
      ([key, value]) => item[key as keyof T] === value
    )
  );
};

export const groupBy = <T>(array: T[], key: keyof T): Record<string, T[]> => {
  return array.reduce((groups, item) => {
    const groupKey = String(item[key]);
    return {
      ...groups,
      [groupKey]: [...(groups[groupKey] || []), item],
    };
  }, {} as Record<string, T[]>);
};

export const getImageUrl = (
  imagePath: string,
  baseUrl: string = "/assets/images/"
): string => {
  if (imagePath.startsWith("http")) return imagePath;
  return `${baseUrl}${imagePath}`;
};

export const generateBreadcrumbs = (
  pathname: string
): { name: string; path: string }[] => {
  const paths = pathname.split("/").filter(Boolean);
  return paths.map((path, index) => ({
    name: path.charAt(0).toUpperCase() + path.slice(1).replace("-", " "),
    path: "/" + paths.slice(0, index + 1).join("/"),
  }));
};

export const validateForm = (
  data: Record<string, any>,
  rules: Record<string, (value: any) => string | null>
): Record<string, string> => {
  const errors: Record<string, string> = {};

  Object.entries(rules).forEach(([field, validator]) => {
    const error = validator(data[field]);
    if (error) {
      errors[field] = error;
    }
  });

  return errors;
};

// Animation utilities
export const fadeIn = (element: HTMLElement, duration: number = 300): void => {
  element.style.opacity = "0";
  element.style.display = "block";

  const startTime = performance.now();

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    element.style.opacity = String(progress);

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };

  requestAnimationFrame(animate);
};

export const slideDown = (
  element: HTMLElement,
  duration: number = 300
): void => {
  element.style.height = "0";
  element.style.overflow = "hidden";
  element.style.display = "block";

  const targetHeight = element.scrollHeight;
  const startTime = performance.now();

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    element.style.height = `${targetHeight * progress}px`;

    if (progress === 1) {
      element.style.height = "auto";
      element.style.overflow = "visible";
    } else {
      requestAnimationFrame(animate);
    }
  };

  requestAnimationFrame(animate);
};
